<script setup lang="ts">
import {onMounted, onUnmounted, ref, useTemplateRef, watch, computed } from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from 'vue-i18n';
import YachtCard from "@/components/Yacht/YachtCard.vue";
import {useSearchStore} from "@/stores/searchStore";
import SearchRangeFilter from "@/pages/SearchResults/filters/SearchRangeFilter.vue";
import SearchSelectionFilter from "@/pages/SearchResults/filters/SearchSelectionFilter.vue";
import SearchCheckboxFilter from "@/pages/SearchResults/filters/SearchCheckboxFilter.vue";
import SearchRemoteSelectionFilter from "@/pages/SearchResults/filters/SearchRemoteSelectionFilter.vue";
import {storeToRefs} from "pinia";
import DateInput from "@/components/Global/DateInput.vue";
import FiltersDrawer from "@/components/FiltersDrawer/FiltersDrawer.vue";
import CustomButton from "@/components/Button/Button.vue";
import { useBodyScrollLock } from "@/composables/useBodyScrollLock";
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";
import {VueSpinner} from 'vue3-spinners';
import {trackEvent} from "@/services/event.service";



const searchStore = useSearchStore();
const {t} = useI18n(); // `t` for translating strings, `n` for numbers (currency)
const route = useRoute();
const router = useRouter();
const isMounted = ref(false);

const pageNum = ref(0);

const scrollComponent = ref(null)

const {selectedFilters} = storeToRefs(searchStore);

const map = useTemplateRef("map-overlay")
const closeMapPopup = () => {
  map.value.classList.add("hidden");
}

const handleScroll = () => {
  let element = scrollComponent.value

  if (searchStore.isLoadingMore) {
    return
  }

  if (!searchStore.paginationData) {
    return
  }

  if (pageNum.value >= searchStore.paginationData.totalPages - 1) {
    return
  }

  if (element.getBoundingClientRect().bottom >= window.innerHeight) {
    return
  }

  pageNum.value++;
  searchStore.loadMore(pageNum.value);

}



const openFilterModal = ref(false);

const isFilterDrawerOpen = computed(() => openFilterModal.value);
useBodyScrollLock(isFilterDrawerOpen);

watch(() => selectedFilters.value, () => {
  searchStore.updatePageUrl(); 
  searchStore.search();
}, { deep: true });

onMounted(async () => {
  console.log("Mounted");
  
  await searchStore.loadFilters(route);
  await searchStore.loadSelections(route);
  
  searchStore.search();
  searchStore.updatePageUrl();
  
  isMounted.value = true;
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll)
})


// Watch for filter modal state and toggle body scroll
watch(openFilterModal, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

// Watch for when no results are detected
watch(() => searchStore.isNoResults, (newVal) => {
  if (newVal) {
    trackEvent('cosmosy.ga.filters.no_results', {
      event_label: 'No search results',
      filters_query: JSON.stringify(searchStore.selectedFilters) // or whatever structure makes sense
    });
  }
});

</script>

<template>
<!--  Here we need to add v-if="!searchStore.isPageLoading" in order not to flicker but there is a bug with the dates so we will revisit it-->
  <div class="mt-28 mb-8 search-results container search-results flex justify-between w-full" @scroll="handleScroll">
    <div class="flex flex-col">
<!--        <SearchRemoteSelectionFilter-->
<!--                                     placeholder="Select Destination"-->
<!--                                     v-model="searchStore.selectedFilters.destinations"-->
<!--                                     :options="searchStore.filters.destinations"-->
<!--                                     :loading="searchStore.destinationsLoading"-->
<!--                                     :remote="searchStore.searchDestinations"-->
<!--  -->
<!--        />-->
<!--  -->
<!--        <hr/>-->
<!--        -->
<!--        <DateInput-->
<!--            v-model:dateFrom="searchStore.selectedFilters.dateFrom"-->
<!--            v-model:dateTo="searchStore.selectedFilters.dateTo"-->
<!--            :has-icon="true"-->
<!--            variant="plain"-->
<!--            @on-date-select="searchStore.setDates"-->
<!--            :show-tabs="false"-->
<!--            :columns="1"-->
<!--            :rows="1"-->
<!--            :calendarClass="'scale-[1.0]'"-->
<!--        >-->
<!--          -->
<!--          <template #display="slotProps">-->
<!--                    <span-->
<!--                        class="border border-gray rounded p-2 flex-1 hover:cursor-pointer"-->
<!--                        @click="slotProps.toggleOpen"-->
<!--                    >-->
<!--                      {{ slotProps.startString }} to {{ slotProps.endString }}-->
<!--                    </span>-->
<!--          </template>-->
<!--        </DateInput>-->
      <div class="search-results__top-bar items-center justify-between flex gap-x-12">
        <div class="flex-col search-results__advanced-title-mobile visible sm:hidden">
          <div class="font-semibold responsive-title-5">{{ t('advancedSearch') }}</div>
        </div>
        <div class="search-results-body__mobile-buttons gap-x-2 visible sm:hidden">
          <button
              class="border transition-[border-color] cursor-pointer h-7.5 px-5 rounded-12xl flex gap-2 items-center relative"
              @click="openFilterModal = !openFilterModal"
          >
            <img src="@/assets/icons/svg/Filters_16x16.svg" alt="filters"/>
          </button>
        </div>
      </div>
      <div v-if="!searchStore.isLoading" class="flex justify-between items-center mt-3 visible sm:hidden">
        <!-- Left: Number of Yachts -->
        <div class="flex items-center gap-1">
          <strong>{{ searchStore.paginationData.totalElements }}</strong>
          {{ t('searchResults.noOfYachts') }}
        </div>

        <!-- Right: Toggle Sort -->
        <div class="flex items-center cursor-pointer h-7.5" @click="searchStore.toggleSort">
<span class="filter-dropdown__text responsive-body-small transition-[border-color] font-bold hover:opacity-60 text-center">
  <span class="block">{{ t('sortPrice') }}</span>
  <span class="block">({{ searchStore.selectedFilters.sort === 'asc' ? t('lowToHigh') : t('highToLow') }})</span>
</span>
          <img v-if="searchStore.selectedFilters.sort === 'asc'" class="ml-2"
               src="@/assets/icons/svg/Down_16x16.svg" alt="Low to High">
          <img v-else class="ml-2" src="@/assets/icons/svg/Up_16x16.svg" alt="High to Low">
        </div>
      </div>
      <div class="search-results__body flex pt-8 lg:pt-0 gap-x-12 w-full">
        <!-- div left -->
        <div class="filters-sidebar flex flex-col gap-y-4 max-w-80"
        >
          <div class="advanced-search_text_parent flex flex-col gap-2">
            <div class="advanced-search__text font-semibold responsive-title-4">{{ t('advancedSearch') }}</div>
            <div class="search-results__title font-bold self-center w-full">
              <CustomButton @click="$router.push({ path: '/recommendations', query: searchStore.recommendationQuery })"
                            type="black"
                            :outlined="false"
                            :rounded="true"
                            class="arrow__btn border transition-all w-full md:py-1 md:px-5 py-1 px-3 flex items-center justify-center rounded-12xl cursor-pointer gap-2">
                {{ t('global.ui.exploreRecommendations') }}</CustomButton>
              
            </div>
          </div>
          <div class="search-results-mobile__title  responsive-body-large font-bold hidden">
            {{
              t('global.ui.exploreRecommendations')
            }}
          </div>
          <div class="filters-sidebar__filters-content flex flex-col gap-y-6 ">
              <div class="flex justify-space-between">
                <span class="font-bold ">{{ t('filters') }}</span>
                <span class="font-bold underline cursor-pointer" @click="searchStore.onClearFilters()">{{
                    t('clearAll')
                  }}</span>
              </div>
              <hr/>
            <div class="flex flex-col gap-y-6 ">
              <SearchRemoteSelectionFilter :title="t('destination')"
                                           event_id="destination"
                                           event_label="Destination"
                                           placeholder="Select Destination"
                                           v-model="searchStore.selectedFilters.destinations"
                                           :options="searchStore.filters.destinations"
                                           :loading="searchStore.destinationsLoading"
                                           :remote="searchStore.searchDestinations"
              />
              <hr/>
  
              <div class="font-bold responsive-title-6"> {{ t('when') }}</div>
  
  
              <DateInput
                  v-model:dateFrom="searchStore.selectedFilters.dateFrom"
                  v-model:dateTo="searchStore.selectedFilters.dateTo"
                  :has-icon="false"
                  variant="plain"
                  @on-date-select="searchStore.setDates"
                  :show-tabs="false"
                  :columns="1"
                  :rows="1"
                  :calendarClass="'scale-[1.0]'"
              >
                <template #display="slotProps">
                    <span
                        class="border border-gray rounded p-2 flex-1 hover:cursor-pointer"
                        @click="slotProps.toggleOpen">
                      {{ slotProps.startString }} to {{ slotProps.endString }}
                    </span>
                </template>
              </DateInput>
              <hr/>
            </div>
            <div class="flex flex-col gap-y-6 "
                 v-if="!searchStore.isInitialSetup && Object.keys(searchStore.filterMetas).length">
              <SearchCheckboxFilter :options="searchStore.filters.categories"
                                    :title="t('categories')"
                                    event_id="type"
                                    event_label="Type"
                                    v-model="searchStore.selectedFilters.categories"
                                    v-if="searchStore.filters.categories"
              />

              <hr v-if="searchStore.filters.categories"/>


              <SearchCheckboxFilter :options="searchStore.filters.charterTypes"
                                    v-model="searchStore.selectedFilters.charterTypes"
                                    :title="t('types')"
                                    event_id="charterTypes"
                                    event_label="Charter Types"
                                    v-if="searchStore.filters.charterTypes"
              />
              <hr v-if="searchStore.filters.charterTypes"/>

              <SearchRangeFilter
                  :title="t('budget')"
                  event_id="budget"
                  event_label="Budget"
                  :range="[searchStore.filterMetas.price.value.min,searchStore.filterMetas.price.value.max]"
                  v-model="searchStore.selectedFilters.price"
                  :bin="searchStore.filterMetas.price.value.bins"
                  type="currency"
                  @update:value="args => { searchStore.selectedFilters.price = args}"
              />

              <hr/>
              <SearchRangeFilter
                  :title="t('yearOfManufacture')"
                  event_id="builtYear"
                  event_label="Built Year"
                  :range="[searchStore.filterMetas.year.value.min,searchStore.filterMetas.year.value.max]"
                  v-model="searchStore.selectedFilters.year"
                  :bin="searchStore.filterMetas.year.value.bins"
                  @update:value="args => { searchStore.selectedFilters.year = args}"
              />

              <hr/>
              <SearchRangeFilter
                  :title="t('cabins')"
                  event_id="cabins"
                  event_label="Cabins"
                  :range="[searchStore.filterMetas.cabin.value.min,searchStore.filterMetas.cabin.value.max]"
                  v-model="searchStore.selectedFilters.cabins"
                  :bin="searchStore.filterMetas.cabin.value.bins"
                  @update:value="args => { searchStore.selectedFilters.cabins = args}"
              />

              <hr/>

              <SearchRangeFilter
                  :title="t('toilets')"
                  event_id="toilets"
                  event_label="Toilets"
                  :range="[searchStore.filterMetas.wc.value.min,searchStore.filterMetas.wc.value.max]"
                  v-model="searchStore.selectedFilters.wc"
                  :bin="searchStore.filterMetas.wc.value.bins"
                  @update:value="args => { searchStore.selectedFilters.wc = args}"
              />

              <hr/>

              <SearchRangeFilter
                  :title="t('berths')"
                  event_id="berths"
                  event_label="Berths"
                  :range="[searchStore.filterMetas.berth.value.min,searchStore.filterMetas.berth.value.max]"
                  v-model="searchStore.selectedFilters.berths"
                  :bin="searchStore.filterMetas.berth.value.bins"
                  @update:value="args => { searchStore.selectedFilters.berths = args}"
              />

              <hr/>

              <SearchRangeFilter
                  :title="t('length')"
                  event_id="length"
                  event_label="Length"
                  :range="[searchStore.filterMetas.length.value.min,searchStore.filterMetas.length.value.max]"
                  v-model="searchStore.selectedFilters.length"
                  :bin="searchStore.filterMetas.length.value.bins"
                  type="length"
                  @update:value="args => { searchStore.selectedFilters.length = args}"
              />

              <hr/>

              <SearchSelectionFilter title="Shipyards"
                                     event_id="shipyards"
                                     event_label="Shipyards"
                                     placeholder="Select filter"
                                     v-model="searchStore.selectedFilters.shipyards"
                                     :options="searchStore.filters.shipyards.map((shipyard) => ({ value: shipyard.id, label: shipyard.name }))"

              />

              <hr/>
              <SearchSelectionFilter :title="t('models')"
                                     placeholder="Select Models"
                                     event_id="boatModels"
                                     event_label="Boat Models"
                                     v-model="searchStore.selectedFilters.models"
                                     :options="searchStore.filters.models.map((model) => ({ value: model.id, label: model.name }))"
              />
              <hr/>
              <SearchCheckboxFilter
                  :options="searchStore.computedOrderedSailItems"
                  v-model="searchStore.selectedFilters.sailTypeIds"
                  :title="t('sailTypes')"
                  event_id="mainsailType"
                  event_label="Mainsail Type"
                  v-if="searchStore.computedOrderedSailItems"
              />
            </div>
            <div v-else class="flex flex-col gap-8">
              <div class="flex justify-between">
                <el-skeleton animated :rows="0"/>
                <el-skeleton class="flex justify-end" animated :rows="0"/>
              </div>
              <hr/>
              <el-skeleton animated :rows="1"/>
              <hr/>
              <el-skeleton animated :rows="1"/>
              <hr/>
              <el-skeleton animated :rows="1"/>
            </div>
          </div>
        </div>
        <!-- div right -->
        <div ref="scrollComponent"   class="scrolling-component results-grid flex flex-col flex-grow min-w-0 w-full gap-8 mt-0 md:mt-5">
          <div v-if="searchStore.paginationData.totalElements"
               class="search-results-body__desktop-buttons flex justify-between items-center w-full">
            <div><strong>{{ searchStore.paginationData.totalElements }}</strong>
              {{ t('searchResults.noOfYachts') }}
            </div>
            <div class="flex gap-x-6">
              <div class="cursor-pointer flex flex-row items-center h-7.5" @click="searchStore.toggleSort">
              <span
                  class="filter-dropdown__text  responsive-body-small transition-[border-color] font-bold hover:opacity-60">
                {{ t('sortPrice') }} ({{
                  searchStore.selectedFilters.sort === 'asc' ? t('lowToHigh') : t('highToLow')
                }})</span>
                <img v-if="searchStore.selectedFilters.sort === 'asc'" class="ml-2"
                     src="@/assets/icons/svg/Down_16x16.svg" alt="Low to High">
                <img v-else class="ml-2" src="@/assets/icons/svg/Up_16x16.svg" alt="High to Low">
              </div>
            </div>
          </div>
          <div v-if="searchStore.isLoading">
            <el-container v-loading="true">
              <div class="boat-result-container boat-results-loading grid grid-cols-3 gap-6">
                <div v-for="index in 5" :key="index" class="border width-fill rounded-8xl flex flex-col gap-2 card">
                  <div
                      class="flex flex-col gap-4 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-8xl p-5">
                    <div class="flex flex-col gap-2">
                      <div class="flex justify-between items-center">
                        <div class=" flex flex-col gap-2 w-3/6">
                          <div class="flex gap-1 cursor-pointer">
                            <img src="@/assets/icons/svg/Location_16x16.svg" alt="location"/>
                            <div>
                              <el-skeleton :rows="0"/>

                            </div>
                          </div>
                        </div>
                        <div class="flex gap-2">
                          <button :class="`border px-4 py-1.5 rounded-12xl h-fit`">
                            <img src="@/assets/icons/svg/Comparison_16x16.svg" alt="comparison"/>
                          </button>
                          <div
                              class="border border-grey transition-[border-color] px-4 py-1.5 rounded-12xl h-fit">
                            <img src="@/assets/icons/svg/heart-icon.svg" alt="add to trip"/>
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-1">
                        <el-skeleton animated :rows="0"/>
                        •
                        <el-skeleton animated :rows="0"/>
                      </div>
                    </div>
                    <div class="boat-content flex flex-row items-center">
                      <div class="boat-image-content h-full w-3/5">
                        <div class="absolute boat-image-content__icon">
                          <img src="@/assets/icons/svg/Boat_plan_24x24.svg" alt="image"/>
                        </div>

                        <el-skeleton-item variant="image"
                                          class="rounded-8xl ship-bg-mask h-full object-cover boatImage"/>
                      </div>
                      <div class="flex flex-col gap-2 ml-auto justify-between w-3/10 boat-stats-container">
                        <div class="flex flex-row items-center gap-2">
                          <img src="@/assets/icons/svg/Bed_24x24.svg" alt="beds"/>
                          <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                        </div>
                        <div class="flex flex-row items-center gap-2">
                          <img src="@/assets/icons/svg/Cabin_24x24.svg" alt="cabins"/>
                          <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                        </div>
                        <div class="flex flex-row items-center gap-2">
                          <img src="@/assets/icons/svg/WC_24x24.svg" alt="bathroom"/>
                          <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                        </div>
                        <div class="flex flex-row items-center gap-2">
                          <img src="@/assets/icons/svg/Length_24x24.svg" alt="length"/>
                          <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                        </div>
                        <div class="flex flex-row items-center gap-2">
                          <img src="@/assets/icons/svg/Engine_24x24.svg" alt="engine"/>
                          <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex place-content-between px-5 py-2.5">
                    <div>
                      <div class="flex flex-row gap-1 items-center">
                        <el-skeleton animated :rows="0"/>

                      </div>
                    </div>
                    <div class="flex flex-row items-center justify-between">
                      <button
                          class="flex flex-row items-center gap-1 bg-black text-white rounded-12xl px-5 py-2">
                        {{ t('viewDetails') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </el-container>
          </div>
          <div v-else-if="searchStore.isError">
            <div class="container flex flex-col justify-center items-center py-20 gap-4">
              <img src="/assets/images/sorry.svg" alt="back" class="2xl:w-18vw xl:w-20vw md:w-30vw sm:w-45vw">
              <div class="font-bold responsive-title-6 text-center">
                {{ t('somethingWentWrong') }}
              </div>
              <div class="text-center">{{ t('search.couldntFindDataDesc') }}</div>
            </div>
          </div>
          <div v-else-if="searchStore.isNoResults">
            <div class="container flex flex-col justify-center items-center py-20 gap-4">
              <img src="/assets/images/search-results.svg" alt="No Results" class="2xl:w-15vw xl:w-17vw md:w-25vw sm:w-35vw">
              <div class="font-bold responsive-title-6 text-center">
                {{ t('search.couldntFindDataDescTitle') }}
              </div>
              <div class="text-center">{{ t('search.couldntFindDataDesc') }}</div>
              <SpecialRequest :t="t" :showSpecialRequestText="false" />
            </div>
          </div>
          <div v-else>
            <el-container style="width: 100%">
              <div class="boat-result-container grid grid-cols-3 gap-6">
                <YachtCard
                    @on-view-details-click="(val) => { router.push({path: `/yachts/${val}`, query: { ...route.query } })}"
                    :yachts="searchStore.availability"
                />
                <!--Can't find -->
                <div v-if="isMounted" class="flex flex-col justify-center items-center h-full gap-4">
                    <img src="/assets/images/search-results.svg" alt="back" class="w-40">
                    <div class="font-bold responsive-title-5 text-center">Can't find what you want?
                    </div>
                    <div class=" responsive-body-medium text-center">Explore our Recommendations, or let our brokers assist you.</div>

                    <CustomButton
                        @click="$router.push({path: '/recommendations', query: route.query})"
                        :rounded="true"
                        :outlined="true"
                        class="w-fit text-semibold hover:text-white hover:bg-black hover:border-black">Explore our Recommendations
                    </CustomButton>
                  <SpecialRequest :t="t" :showSpecialRequestText="false" />
                  </div>
              </div>
            </el-container>
            
            <div class="flex justify-center mt-6" v-if="searchStore.isLoadingMore">
              <div class="flex justify-center items-center min-h-[300px]">
                <div class="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>



  <!-- Page Loading Spinner -->
  <div v-if="searchStore.isPageLoading" class="flex justify-center items-center min-h-screen">
    <VueSpinner size="50" class="p-2" color="#1B84FF"/>
  </div>
  <!-- Filter Drawer -->
  <FiltersDrawer :openFilterModal="openFilterModal" @close="openFilterModal = false" />

  <div id="map-overlay" ref="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
    <div id="map-overlay-map" class="map-popup-map relative" @click.stop>
      <button
          class="absolute top-6 right-5 z-50 bg-white p-3 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-200 transition-all duration-300"
          @click="closeMapPopup"
      >
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="" class="w-5 h-5">
      </button>
    </div>
  </div>
  
</template>

<style scoped>
.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
}

.hidden {
  display: none;
}

.border-black {
  border-color: #000 !important;
}

.arrow__btn:hover img {
  filter: brightness(0) invert(1);
}

:deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #000 inset;
}

:deep(.el-select__wrapper.is-filterable .el-select__input:focus-visible) {
  outline: none;
}

:deep(.el-select__selected-item.el-select__input-wrapper) {
  box-shadow: unset;
}

:deep(.el-tag.el-tag--info) {
  background: black;
  --el-tag-text-color: #fff;
  font-size: 14px;
}

:deep(.tabs .el-skeleton) {
  width: 5vw;
}

:deep(.tabs .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.boat-result-container .el-skeleton) {
  width: 8vw;
}

:deep(.boat-result-container .el-skeleton__item.el-skeleton__p ) {
  width: 6.5vw;
}

:deep(.boat-stats-container .el-skeleton) {
  width: 6vw;
}

:deep(.boat-stats-container .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.el-loading-mask) {
  @apply -mt-4 -mr-2 z-20;
}


.border-black {
  border-color: #000000 !important;
}

.border-grey:hover {
  border-color: #000000 !important;
}

.notification-bubble {
  background-color: #000000; /* Bubble background color */
  color: white; /* Text color */
  font-size: 12px; /* Text size */
  width: 20px; /* Width of the bubble */
  height: 20px; /* Height of the bubble */
  border-radius: 50%; /* Make the bubble round */
  position: absolute; /* Positioning relative to the parent */
  right: 0px; /* Distance from the right edge of the parent */
  top: -12px; /* Positioning the bubble slightly above the top edge */
}

.more-filter-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.more-filter-enter-active {
  transition: all .3s ease-out;
}

.more-filter-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.boat-image-content__icon {
  width: 24px;
  height: 24px;
  padding-right: 2px;
}

.boatImage {
  margin-left: 3px;
  margin-top: 3px;
  min-height: 10.5rem;
}

.loading-text:after {
  content: ' .';
  animation: dots 1.75s steps(5, end) infinite;
}

@keyframes dots {
  0%, 20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: .25em 0 0 rgba(0, 0, 0, 0),
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: black;
    text-shadow: .25em 0 0 rgba(0, 0, 0, 0),
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: .25em 0 0 black,
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  80%, 100% {
    text-shadow: .25em 0 0 black,
    .5em 0 0 black;
  }
}


.filters-sidebar {
  width: 40vw!important;
}

.text-location {
  text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
}

.tab:not(.selected-tab) {
  transform: scale(.97);
  transform-origin: left;
}



@media (max-width: 1735px) {
  .boat-result-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1625px ) {
  .filters-sidebar {
    width: 40vw!important;
  }
  

  .advanced-search__text {
    font-size: 1.25rem;
  }
}

@media (max-width: 1385px ) {
  .search-results__advanced-title__text {
    font-size: 1.25rem
  }

  .filters-sidebar {
    width: 30vw!important;
    min-width: 25vw;
  }


}


@media (max-width: 1320px) {
  .search-results {
    margin-top: 5rem;
  }
}


@media (max-width: 1110px) {
  .boat-result-container {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .filter-dropdown__text {
    font-size: .875rem;
  }
}

@media (max-width: 1110px) and (min-width: 885px) {
  .boat-stats-container {
    margin-left: 5vw !important;
    row-gap: 1rem;
  }

  .boat-stats-container span {
    font-size: 1rem !important;
  }
}

@media (max-width: 1024px ) {
  .filters-sidebar {
    width: 30vw!important;
    min-width: 30vw;
  }
}



@media (max-width: 767px) {

  .search-results-body__desktop-buttons {
    display: none;
  }
  .boat-result-container {
    width: 100%;
  }

  .advanced-search__text {
    display: none;
  }

  .search-results,
  .search-results__body {
    padding-top: 1.5rem !important;
  }

  .search-results__body {
    flex-direction: column-reverse;
  }

  .filters-sidebar {
    width: 100%;
    padding-top: 2.25rem !important;
    row-gap: unset;
    display: none;
  }

  .filters-sidebar__filters-content {
    display: none;
  }

  .search-results-sidebar__buttons {
    padding-top: 1rem !important;
  }

  .search-results__title {
    display: none;
  }

  .search-results__top-bar {
    -moz-column-gap: unset;
    column-gap: unset;
  }
  
  .search-results-mobile__title {
    display: block;
  }

  .special-offer__days {
    display: none;
  }

  .search-results {
    margin-top: 5rem;
  }

}

@media (max-width: 768px) {
  .boatImage {
    max-height: 200px;
    object-fit: cover;
    width: 100%;
  }

}

@media (max-width: 768px) and (min-width: 585px) {
  .boat-stats-container {
    width: 35%;
  }
}

@media (max-width: 450px) {
  .boat-content {
    flex-direction: column !important;
    row-gap: 0.75rem;

  }

  .boat-image-content {
    width: 100%;
  }

  .boat-stats-container {
    margin: unset !important;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    row-gap: 2rem;
  }

  .boat-stats-container div {
    flex-direction: column !important;
  }
}

.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 80;
  transition: opacity 0.3s ease;
}

.filter-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100%;
  background-color: #fff;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  z-index: 90;
  padding: 30px;
  overflow-y: auto;
}

.filter-drawer.open {
  transform: translateX(0);
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.btn-close {
  border: none;
  background: none;
  cursor: pointer;
}

.filters-sidebar__filters-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (max-width: 768px) {
  .filter-drawer {
    width: 95%;
    border-radius: 20px 0 0 20px;
  }
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0rem;
  background-color: #fff;
}

.btnClose {
  border: 1px solid #B7B6BE;
  padding: 6px 16px;
  border-radius: 20px;
}

.no-scroll {
  overflow: hidden;
  height: 100vh;
  position: fixed;
  width: 100%;
}

</style>